import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import type { BotLevel, Lang, ModeId } from '@polyglot/engine';
import {
  BOT_LEVELS,
  CURRENT_RULES,
  isModeId,
  LANGS,
  languageRequirement,
  MODES,
  validateLanguageSelection,
} from '@polyglot/engine';
import { BackLink, LangName, Spinner } from '../components/ui';
import { DrawUnavailableError, startLocalSession } from '../game/localSession';
import { updateSettings, useSettings } from '../lib/settings';

export function LanguagePicker({
  value,
  onChange,
  max,
  label,
}: {
  value: Lang[];
  onChange: (v: Lang[]) => void;
  max: number;
  label: string;
}) {
  const toggle = (l: Lang) => {
    if (value.includes(l)) onChange(value.filter((x) => x !== l));
    else if (max === 1) onChange([l]);
    else if (value.length < max) onChange([...value, l]);
  };
  return (
    <fieldset className="lang-picker">
      <legend>{label}</legend>
      {LANGS.map((l) => (
        <button
          type="button"
          key={l}
          className={`chip big${value.includes(l) ? ' selected' : ''}`}
          aria-pressed={value.includes(l)}
          onClick={() => toggle(l)}
        >
          <LangName lang={l} />
          {value.includes(l) && max > 1 && <span className="order">{value.indexOf(l) + 1}</span>}
        </button>
      ))}
    </fieldset>
  );
}

export function ContractPreview({ mode, langs }: { mode: ModeId; langs: Lang[] }) {
  const { t } = useTranslation();
  const m = MODES[mode];
  if (validateLanguageSelection(m, langs) !== null) return null;
  const req = languageRequirement(m, langs, CURRENT_RULES);
  if (req.kind === 'none') return <p className="muted">{t('setup.noLanguageBonus')}</p>;
  const name = req.contract ? t(`contracts.${req.contract}.name`) : t('contracts.diversity.name');
  const desc = req.contract
    ? t(`contracts.${req.contract}.desc`)
    : t('contracts.diversity.desc', { pct: req.bonusBp / 100 });
  return (
    <p className="contract-preview">
      <strong>{name}</strong> (+{req.bonusBp / 100} %) — {desc}
    </p>
  );
}

export function Setup({ params }: { params: { mode?: string } }) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const settings = useSettings();
  const initialMode: ModeId = isModeId(params.mode) ? params.mode : 'classic';
  const [mode, setMode] = useState<ModeId>(initialMode);
  const def = MODES[mode];
  const [langs, setLangs] = useState<Lang[]>(() =>
    settings.lastLanguages.slice(0, def.maxLanguages),
  );
  const [bot, setBot] = useState<BotLevel | 'none'>(mode === 'duel' ? 'casual' : 'none');
  const [botSame, setBotSame] = useState(true);
  const [botLangs, setBotLangs] = useState<Lang[]>(['en']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectionError = validateLanguageSelection(def, langs);
  const botSelectionError =
    bot !== 'none' && !botSame && def.languagePolicy === 'individual'
      ? validateLanguageSelection(def, botLangs)
      : null;

  const changeMode = (m: ModeId) => {
    setMode(m);
    const d = MODES[m];
    if (langs.length > d.maxLanguages) setLangs(langs.slice(0, d.maxLanguages));
    if (m === 'polyglot' && langs.length < 2)
      setLangs(langs.length === 1 ? [langs[0]!, langs[0] === 'en' ? 'fr' : 'en'] : ['fr', 'en']);
    if (m === 'relax') setBot('none');
  };

  const start = async () => {
    if (selectionError || botSelectionError) return;
    setLoading(true);
    setError(null);
    try {
      updateSettings({ lastLanguages: langs });
      const robotLangs = def.languagePolicy === 'shared' || botSame ? langs : botLangs;
      await startLocalSession({
        mode,
        languages: langs,
        bot: bot === 'none' ? null : { level: bot, languages: robotLangs },
      });
      navigate('/play');
    } catch (e) {
      setError(e instanceof DrawUnavailableError ? t('setup.noDraw') : t('errors.lexiconLoad'));
      setLoading(false);
    }
  };

  return (
    <main className="page setup">
      <BackLink />
      <h1>{t('setup.title')}</h1>
      <fieldset className="mode-picker">
        <legend>{t('setup.mode')}</legend>
        {(['classic', 'polyglot', 'duel', 'sprint', 'relax'] as const).map((m) => (
          <button
            type="button"
            key={m}
            className={`mode-card${m === mode ? ' selected' : ''}`}
            aria-pressed={m === mode}
            onClick={() => changeMode(m)}
          >
            <strong>{t(`modes.${m}.name`)}</strong>
            <span className="muted small">{t(`modes.${m}.short`)}</span>
          </button>
        ))}
      </fieldset>
      <p className="mode-desc">{t(`modes.${mode}.desc`)}</p>

      <LanguagePicker
        value={langs}
        onChange={setLangs}
        max={def.maxLanguages}
        label={
          def.languagePolicy === 'shared'
            ? t('setup.languagesShared', { min: def.minLanguages, max: def.maxLanguages })
            : t('setup.languagesIndividual')
        }
      />
      {selectionError && (
        <p className="muted small">
          {t(`setup.errors.${selectionError}`, { min: def.minLanguages, max: def.maxLanguages })}
        </p>
      )}
      <ContractPreview mode={mode} langs={langs} />

      {mode !== 'relax' && (
        <fieldset className="bot-picker">
          <legend>{t('setup.opponent')}</legend>
          <div className="row wrap">
            <button
              type="button"
              className={`chip${bot === 'none' ? ' selected' : ''}`}
              aria-pressed={bot === 'none'}
              onClick={() => setBot('none')}
            >
              {t('setup.noOpponent')}
            </button>
            {BOT_LEVELS.map((b) => (
              <button
                type="button"
                key={b}
                className={`chip${bot === b ? ' selected' : ''}`}
                aria-pressed={bot === b}
                onClick={() => setBot(b)}
              >
                ⚙ {t(`bots.${b}`)}
              </button>
            ))}
          </div>
          {bot !== 'none' && (
            <>
              <p className="muted small">{t('setup.robotNotice')}</p>
              <p className="muted small">{t(`bots.${bot}Desc`)}</p>
              {def.languagePolicy === 'individual' && (
                <>
                  <label className="row">
                    <input
                      type="checkbox"
                      checked={botSame}
                      onChange={(e) => setBotSame(e.target.checked)}
                    />
                    {t('setup.robotSameLanguages')}
                  </label>
                  {!botSame && (
                    <LanguagePicker
                      value={botLangs}
                      onChange={setBotLangs}
                      max={3}
                      label={t('setup.robotLanguages')}
                    />
                  )}
                </>
              )}
            </>
          )}
        </fieldset>
      )}

      {error && (
        <p className="message error" role="alert">
          {error}
        </p>
      )}
      {loading ? (
        <Spinner label={t('setup.loading')} />
      ) : (
        <button
          className="btn primary big"
          disabled={!!selectionError || !!botSelectionError}
          onClick={() => void start()}
        >
          {t('setup.start')}
        </button>
      )}
    </main>
  );
}
